import { useEffect, useRef, useState } from 'react'
import { GameEngine } from '@/gameplay/GameEngine'
import { getMap } from '@/maps'
import { useAppStore } from '@/hud-bridge/store'
import { GameHud } from './hud/GameHud'
import { MobileControls } from './hud/MobileControls'
import { PauseOverlay } from './screens/PauseOverlay'
import { SettingsScreen } from './screens/RecordsSettings'

interface Props {
  active: boolean
  mapId: string
  mode: 'exit' | 'clear'
  seed?: number
}

export function GameCanvas({ active, mapId, mode, seed }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const engineRef = useRef<GameEngine | null>(null)
  const [pauseSettings, setPauseSettings] = useState(false)
  const settings = useAppStore((s) => s.settings)
  const setHud = useAppStore((s) => s.setHud)
  const setResult = useAppStore((s) => s.setResult)
  const setScreen = useAppStore((s) => s.setScreen)
  const setEngineReady = useAppStore((s) => s.setEngineReady)
  const hud = useAppStore((s) => s.hud)
  const screen = useAppStore((s) => s.screen)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const engine = new GameEngine(canvas, useAppStore.getState().settings)
    engineRef.current = engine
    setEngineReady(true)

    const unHud = engine.bus.on('hud', (view) => {
      setHud(view)
      if (view.phase === 'paused') setScreen('paused')
    })
    const unWon = engine.bus.on('won', (result) => {
      setResult(result)
      setScreen('victory')
    })
    const unLost = engine.bus.on('lost', (result) => {
      setResult(result)
      setScreen('defeat')
    })

    return () => {
      unHud()
      unWon()
      unLost()
      engine.dispose()
      engineRef.current = null
      setEngineReady(false)
      setHud(null)
    }
  }, [setEngineReady, setHud, setResult, setScreen])

  useEffect(() => {
    engineRef.current?.updateSettings(settings)
  }, [settings])

  useEffect(() => {
    if (!active) return
    const map = getMap(mapId)
    const engine = engineRef.current
    if (!map || !engine) return
    engine.startRound(map, mode, seed ?? Date.now())
    setScreen('playing')
    setPauseSettings(false)
  }, [active, mapId, mode, seed, setScreen])

  useEffect(() => {
    if (screen === 'paused') engineRef.current?.pause()
    if (screen !== 'paused') setPauseSettings(false)
  }, [screen])

  const showHud = active && (screen === 'playing' || screen === 'paused')
  const isTouch = engineRef.current?.isTouch ?? false

  return (
    <>
      <canvas ref={canvasRef} className="game-canvas" />
      {showHud && hud && <GameHud hud={hud} isTouch={isTouch} />}
      {showHud && isTouch && engineRef.current && (
        <MobileControls
          adapter={engineRef.current.mobile}
          leftHand={settings.leftHandMobile}
          disabled={screen === 'paused'}
        />
      )}
      {screen === 'paused' && !pauseSettings && (
        <PauseOverlay
          onResume={() => {
            engineRef.current?.resume()
            setScreen('playing')
          }}
          onRestart={() => {
            const map = getMap(mapId)
            if (map && engineRef.current) {
              engineRef.current.startRound(map, mode, Date.now())
              setScreen('playing')
            }
          }}
          onSettings={() => setPauseSettings(true)}
          onMenu={() => {
            engineRef.current?.pause()
            setScreen('menu')
          }}
        />
      )}
      {screen === 'paused' && pauseSettings && (
        <div className="dim overlay" style={{ zIndex: 16 }}>
          <SettingsScreen onDone={() => setPauseSettings(false)} />
        </div>
      )}
    </>
  )
}
